import { GETREQUESTOPTIONS, instance } from '../apiService';
import { DocumentStatus } from '../../components/documents/types';
import type { Category } from '../../components/ui-components/category/categoryColors';
import type { PolicyConditionalsValue } from '../../components/department-policies/conditionals/types';

export type OwnerType =
    'department' | 'implant' | 'organization' | 'nordinsight';

export interface PaginatedResult<T> {
    data: T[];
    total: number;
    page: number;
    limit: number;
}

export interface PopulatedApproval {
    approvedBy: { name?: string; role?: string; isMine?: boolean };
    approvedAt: string;
    comment?: string;
}

export interface ManagedPolicyCounts {
    draft: number;
    completed: number;
    expired: number;
    pending: number;
}

export interface ManagedPoliciesResult<T> extends PaginatedResult<T> {
    counts: ManagedPolicyCounts;
}

export interface PolicyDocument {
    _id: string;
    schema_id: string;
    schemaKey: string;
    schemaVersion: number;
    title?: string;
    status: DocumentStatus;
    data: Record<string, any>;
    createdAt: string;
    updatedAt: string;
}

export interface CoAuthor {
    user_id?: string;
    name?: string;
}

export interface PopulatedPolicy {
    _id: string;
    document_id: PolicyDocument;
    ownerType: OwnerType;
    effectiveDate: string;
    expiryDate: string;
    isTemplate: boolean;
    tags?: string[];
    attachments: string[];
    coAuthors?: CoAuthor[];
    conditionals?: PolicyConditionalsValue;
    editLog: {
        action: string;
        userName: string;
        comment?: string;
        date: string;
    }[];
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
    requireApproval?: boolean;
    approval?: PopulatedApproval;
}

export interface PolicyListItem {
    _id: string;
    ownerType: OwnerType;
    effectiveDate?: string;
    expiryDate?: string;
    tags?: string[];
    title?: string;
    status?: DocumentStatus;
    createdBy: string;
    updatedBy?: string;
    createdAt: string;
    updatedAt: string;
    requireApproval?: boolean;
}

export interface PolicyGroupListItem {
    _id: string;
    title: string;
    parentId?: string;
    parentTitle?: string;
}

export interface PolicyRoomLink {
    _id: string;
    Name: string;
    Model: string;
    Manufacturer?: string;
    FieldStrength: number;
    location?: string;
    category?: Category;
    linkedOnRoom: boolean;
    equipment: { _id: string; name: string; type: string }[];
}

export interface CreateDepartmentPolicyInput {
    schemaKey: string;
    effectiveDate?: string;
    expiryDate?: string;
    tags?: string[];
    coAuthors?: CoAuthor[];
    requireApproval?: boolean;
}

export interface UpdatePolicyInput {
    effectiveDate?: string;
    expiryDate?: string;
    tags?: string[];
    coAuthors?: CoAuthor[];
    attachments?: string[];
    conditionals?: PolicyConditionalsValue;
    status?: DocumentStatus;
    requireApproval?: boolean;
}

export interface SavePolicyDocumentInput extends UpdatePolicyInput {
    title?: string;
    comment?: string;
    status?: DocumentStatus;
}

export type ManagedPolicyTab = 'draft' | 'completed' | 'expired' | 'pending';

// GET /api/policies — completed department policies
export const getCompletedPolicies = async (params?: {
    page?: number;
    limit?: number;
}): Promise<PaginatedResult<PolicyListItem>> => {
    const res = await instance.get('/api/policies', {
        ...GETREQUESTOPTIONS(),
        params,
    });
    return res.data;
};

// GET /api/policies/managed — all department policies for admins
export const getManagedPolicies = async (params?: {
    page?: number;
    limit?: number;
    tab?: ManagedPolicyTab;
}): Promise<ManagedPoliciesResult<PolicyListItem>> => {
    const res = await instance.get('/api/policies/managed', {
        ...GETREQUESTOPTIONS(),
        params,
    });
    return res.data;
};

export const getPolicy = async (id: string): Promise<PopulatedPolicy> => {
    const res = await instance.get(`/api/policies/${id}`, GETREQUESTOPTIONS());
    return res.data;
};

export const getGroupsForPolicy = async (
    policyId: string
): Promise<PolicyGroupListItem[]> => {
    const res = await instance.get(
        `/api/policies/${policyId}/groups`,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const getRoomsForPolicy = async (
    policyId: string
): Promise<PolicyRoomLink[]> => {
    const res = await instance.get(
        `/api/policies/${policyId}/rooms`,
        GETREQUESTOPTIONS()
    );
    if (res.status !== 200) {
        throw res.data;
    }
    return res.data;
};

export const linkRoomToPolicy = async (
    policyId: string,
    machineId: string,
    equipmentId?: string
): Promise<boolean> => {
    const res = await instance.post(
        `/api/policies/${policyId}/link-room`,
        { machineId, equipmentId },
        GETREQUESTOPTIONS()
    );
    return res.status === 204;
};

export const unlinkRoomFromPolicy = async (
    policyId: string,
    machineId: string,
    equipmentId?: string
): Promise<boolean> => {
    const res = await instance.post(
        `/api/policies/${policyId}/unlink-room`,
        { machineId, equipmentId },
        GETREQUESTOPTIONS()
    );
    return res.status === 204;
};

export const createDepartmentPolicy = async (
    data: CreateDepartmentPolicyInput
): Promise<PopulatedPolicy> => {
    const res = await instance.post('/api/policies', data, GETREQUESTOPTIONS());
    return res.data;
};

export const updatePolicy = async (
    id: string,
    data: UpdatePolicyInput
): Promise<PopulatedPolicy> => {
    const res = await instance.put(
        `/api/policies/${id}`,
        data,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const deletePolicy = async (id: string): Promise<void> => {
    await instance.delete(`/api/policies/${id}`, GETREQUESTOPTIONS());
};

// POST /api/policies/:id/create-from-template
export const createFromTemplate = async (
    templatePolicyId: string
): Promise<PopulatedPolicy> => {
    const res = await instance.post(
        `/api/policies/${templatePolicyId}/create-from-template`,
        {},
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const savePolicyDocument = async (
    policyId: string,
    data: Record<string, any>,
    title?: string,
    comment?: string,
    policy?: UpdatePolicyInput
): Promise<PolicyDocument> => {
    const res = await instance.put(
        `/api/policies/${policyId}/document`,
        { data, title, comment, ...policy },
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const completePolicyDocument = async (
    policyId: string,
    data: Record<string, any>,
    title?: string,
    comment?: string,
    policy?: UpdatePolicyInput
): Promise<PolicyDocument> => {
    const res = await instance.post(
        `/api/policies/${policyId}/document/complete`,
        { data, title, comment, ...policy },
        GETREQUESTOPTIONS()
    );
    return res.data;
};

// GET /api/policies/templates
export const getNordInsightTemplates = async (params?: {
    page?: number;
    limit?: number;
}): Promise<PaginatedResult<PopulatedPolicy>> => {
    const res = await instance.get('/api/policies/templates', {
        ...GETREQUESTOPTIONS(),
        params,
    });
    return res.data;
};

// GET /api/policies/admin/templates
export const getNordInsightAdminTemplates = async (params?: {
    page?: number;
    limit?: number;
}): Promise<PaginatedResult<PopulatedPolicy>> => {
    const res = await instance.get('/api/policies/admin/templates', {
        ...GETREQUESTOPTIONS(),
        params,
    });
    return res.data;
};

export const getNordInsightAdminTemplate = async (
    id: string
): Promise<PopulatedPolicy> => {
    const res = await instance.get(
        `/api/policies/admin/templates/${id}`,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const getNordInsightAdminSchema = async (): Promise<
    Record<string, any>
> => {
    const res = await instance.get(
        '/api/policies/admin/schema',
        GETREQUESTOPTIONS()
    );
    return res.data;
};

// GET /api/policies/schema
export const getPolicySchema = async (): Promise<Record<string, any>> => {
    const res = await instance.get('/api/policies/schema', GETREQUESTOPTIONS());
    return res.data;
};

// POST /api/policies/admin/templates
export const createNordInsightTemplate = async (data: {
    schemaKey: string;
    schemaVersion?: number;
    title?: string;
    effectiveDate?: string;
    expiryDate?: string;
}): Promise<PopulatedPolicy> => {
    const res = await instance.post(
        '/api/policies/admin/templates',
        data,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const saveNordInsightTemplateDocument = async (
    policyId: string,
    data: Record<string, any>,
    title?: string,
    comment?: string,
    policy?: UpdatePolicyInput
): Promise<PolicyDocument> => {
    const res = await instance.put(
        `/api/policies/admin/templates/${policyId}/document`,
        { data, title, comment, ...policy },
        GETREQUESTOPTIONS()
    );
    return res.data;
};

export const completeNordInsightTemplateDocument = async (
    policyId: string,
    data: Record<string, any>,
    title?: string,
    comment?: string,
    policy?: UpdatePolicyInput
): Promise<PolicyDocument> => {
    const res = await instance.post(
        `/api/policies/admin/templates/${policyId}/document/complete`,
        { data, title, comment, ...policy },
        GETREQUESTOPTIONS()
    );
    return res.data;
};

// DELETE /api/policies/admin/templates/:id
export const deleteNordInsightTemplate = async (id: string): Promise<void> => {
    await instance.delete(
        `/api/policies/admin/templates/${id}`,
        GETREQUESTOPTIONS()
    );
};

// GET /api/policies/from-implant/:implantId — get policies linked to an implant (for implant page)
export const getPoliciesFromLinkedImplant = async (
    implantId: string
): Promise<PolicyListItem[]> => {
    const res = await instance.get(
        `/api/policies/from-implant/${implantId}`,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

// GET /api/policies/:id/linked-implants — get implants linked to a policy
export const getLinkedImplants = async (policyId: string) => {
    const res = await instance.get(
        `/api/policies/${policyId}/linked-implants`,
        GETREQUESTOPTIONS()
    );
    return res.data;
};

// POST /api/policies/:id/link-implant — link a department policy to an implant
export const linkImplant = async (
    policyId: string,
    implantId: string
): Promise<void> => {
    await instance.post(
        `/api/policies/${policyId}/link-implant`,
        { implantId },
        GETREQUESTOPTIONS()
    );
};

// POST /api/policies/:id/unlink-implant — unlink a department policy from an implant
export const unlinkImplant = async (
    policyId: string,
    implantId: string
): Promise<void> => {
    await instance.post(
        `/api/policies/${policyId}/unlink-implant`,
        { implantId },
        GETREQUESTOPTIONS()
    );
};

// GET /api/policies/pending-approval — list department policies awaiting sign-off
export const getPendingApprovalPolicies = async (params?: {
    page?: number;
    limit?: number;
}): Promise<PaginatedResult<PolicyListItem>> => {
    const res = await instance.get('/api/policies/pending-approval', {
        ...GETREQUESTOPTIONS(),
        params,
    });
    return res.data;
};

// POST /api/policies/:id/approve — approve a pending department policy
export const approvePolicy = async (
    policyId: string,
    comment?: string
): Promise<PopulatedPolicy> => {
    const res = await instance.post(
        `/api/policies/${policyId}/approve`,
        comment ? { comment } : {},
        GETREQUESTOPTIONS()
    );
    return res.data;
};

// PUT /api/policies/:id/approval-comment — edit the approval comment (approver only)
export const editPolicyApprovalComment = async (
    policyId: string,
    comment: string
): Promise<PopulatedPolicy> => {
    const res = await instance.put(
        `/api/policies/${policyId}/approval-comment`,
        { comment },
        GETREQUESTOPTIONS()
    );
    return res.data;
};

// DELETE /api/policies/:id/approval — remove the current approval (approver only)
export const removePolicyApproval = async (
    policyId: string
): Promise<PopulatedPolicy> => {
    const res = await instance.delete(
        `/api/policies/${policyId}/approval`,
        GETREQUESTOPTIONS()
    );
    return res.data;
};
